import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/i18n";

const meta = [
  { img: "/images/esercito.jpg", link: "/titoli" },
  { img: "/images/cruz-roja-van.webp" },
  { img: "/images/yuri-suit.jpg" },
  { img: "/images/vodafone.jpeg" },
  { img: "/images/yuma-office.webp" },
  { img: "/images/notarchain-stage.jpg", link: "/yurax" },
  { img: "/images/soluzione-albania-team.webp" },
  { img: "/images/arbitro-figc.webp" },
  { img: "/images/manager-poliedrico.jpg" },
];

export const Cronostoria = () => {
  const { t } = useLang();
  const chapters = t.cronostoria.chapters.map((c, i) => ({ ...c, ...meta[i] }));

  return (
    <section id="cronostoria" className="relative py-24 sm:py-32 bg-gradient-to-b from-[#F0F9FF] via-[#F7FBFD] to-[#ECFEFF]" data-testid="cronostoria-section">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="text-center max-w-2xl mx-auto"
        >
          <span className="font-mono-ed text-xs uppercase tracking-[0.22em] text-[#0284C7]">{t.cronostoria.label}</span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0B2530] mt-4 leading-tight">
            {t.cronostoria.h2a}<em className="font-serif-ed italic font-medium text-[#06B6D4]">{t.cronostoria.h2em}</em>{t.cronostoria.h2b}
          </h2>
          <p className="text-[#3D5A66] mt-5 text-base sm:text-lg">
            {t.cronostoria.sub}
          </p>
        </motion.div>

        <div className="relative mt-20">
          <div className="absolute left-4 sm:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#7DD3FC] via-[#06B6D4] to-[#A5F3FC]" aria-hidden="true" />
          {chapters.map((c, i) => (
            <motion.article
              key={c.title}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-70px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative pl-14 sm:pl-0 sm:grid sm:grid-cols-2 sm:gap-16 mb-20 last:mb-0"
              data-testid={`cronostoria-chapter-${i}`}
            >
              <span className={`absolute left-4 sm:left-1/2 top-2 -translate-x-1/2 w-4 h-4 rounded-full bg-white border-[3px] shadow-md ${i % 3 === 1 ? "border-[#F0A05C] shadow-orange-300/50" : "border-[#06B6D4] shadow-cyan-300/50"}`} aria-hidden="true" />
              <div className={i % 2 === 0 ? "sm:text-right sm:pr-4" : "sm:order-2 sm:pl-4"}>
                <span className="font-mono-ed text-[11px] uppercase tracking-[0.2em] text-[#0284C7]">{c.era} · {c.place}</span>
                <h3 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B2530] mt-3 leading-tight">{c.title}</h3>
                <p className="font-serif-ed text-lg sm:text-xl text-[#3D5A66] leading-relaxed mt-4">{c.text}</p>
                {c.link && (
                  <Link to={c.link} data-testid={c.link === "/yurax" ? "cronostoria-link-yurax" : `cronostoria-link-chapter-${i}`}
                    className={`group inline-flex items-center gap-2 mt-5 text-sm font-semibold text-[#0284C7] hover:text-[#E8894B] transition-colors duration-300 ${i % 2 === 0 ? "sm:flex-row-reverse" : ""}`}>
                    {c.linkText || t.cronostoria.linkLabel}
                    <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                )}
              </div>
              <div className={`mt-8 sm:mt-0 ${i % 2 === 0 ? "sm:order-2 sm:pl-4" : "sm:order-1 sm:pr-4"}`}>
                {c.img && (
                  <div className="rounded-3xl overflow-hidden img-frame border border-white">
                    <img src={c.img} alt={c.title} loading="lazy"
                      className="w-full h-64 sm:h-72 object-cover object-top hover:scale-[1.03] transition-transform duration-700" />
                  </div>
                )}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
